import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Eye,
  Lock,
  MailWarning,
  Plus,
  RefreshCw,
  Scale,
  Search,
  Send,
  Trash2,
} from "lucide-react";

import {
  useAddRfqLine,
  useAddRfqSupplier,
  useCancelRfq,
  useFinalizeRfq,
  useInventory,
  useRefreshRfqLine,
  useRemoveRfqLine,
  useRemoveRfqSupplier,
  useEmailStatus,
  useRfq,
  useRfqDispatch,
  useRfqDispatchPreview,
  useRfqOutboundMessages,
  useSendRfq,
  useSetRfqAllocation,
  useSuppliers,
} from "@/api/queries";
import type {
  EmailStatus,
  OutboundMessage,
  RfqDetail,
  RfqDispatchRow,
  RfqLine,
  RfqStaleLine,
  RfqSupplierRow,
  UnitCode,
  Uuid,
} from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatDate,
  formatDateTime,
  formatDispatchState,
  formatQuantity,
  formatRfqLineSource,
  formatRfqStatus,
  formatRfqTerm,
  formatRfqWarning,
  formatSendError,
  pluralize,
} from "@/lib/format";
import { dispatchTone, rfqTone } from "@/lib/stock";
import { STOCKABLE_UNITS } from "@/lib/units";

/**
 * Une demande de prix (V1.6-04).
 *
 * Deux moments, et l'écran ne doit jamais les confondre : tant que c'est un
 * brouillon tout se modifie ; une fois figée, le contenu est arrêté — et
 * **toujours pas envoyé**. Il n'y a pas de bouton « Envoyer » sur cette page
 * parce qu'il n'y a rien derrière : la transmission arrive dans une version
 * suivante, et prétendre le contraire ferait croire à quelqu'un qu'un
 * fournisseur a reçu la demande.
 */
export function RfqDetailPage() {
  const { rfqId = "" } = useParams();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useRfq(rfqId);
  const finalize = useFinalizeRfq();
  const cancel = useCancelRfq();
  const [adding, setAdding] = useState(false);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { rfq, lines, suppliers, validation } = data;
  const draft = rfq.status === "DRAFT";

  async function confirmFinalize() {
    try {
      await finalize.mutateAsync({ rfqId });
      toast.success(
        "Demande figée",
        "Le contenu est arrêté. Elle n'a pas été envoyée — transmettez-la vous-même.",
      );
    } catch (failure) {
      toast.error("Demande non figée", String(failure));
    }
  }

  async function confirmCancel() {
    try {
      await cancel.mutateAsync({ rfqId });
      toast.success("Demande annulée", "Son contenu reste lisible.");
    } catch (failure) {
      toast.error("Demande non annulée", String(failure));
    }
  }

  return (
    <Page>
      <PageHeader
        title={rfq.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[12px]">{rfq.code}</span>
            <StatusBadge tone={rfqTone(rfq.status)}>{formatRfqStatus(rfq.status)}</StatusBadge>
            <span className="text-ink-subtle">
              {pluralize(lines.length, "ligne")} · {pluralize(suppliers.length, "fournisseur")}
            </span>
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setPreviewing(true)}>
              <Eye className="size-4" aria-hidden />
              Aperçu
            </Button>
            {draft && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!validation.can_finalize || finalize.isPending}
                  onClick={() => void confirmFinalize()}
                >
                  <Lock className="size-4" aria-hidden />
                  {finalize.isPending ? "…" : "Figer le contenu"}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => void confirmCancel()}>
                  <Ban className="size-4" aria-hidden />
                  Annuler
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/rfqs"
          className="inline-flex items-center gap-1.5 text-[13px] text-ink-subtle hover:text-ink"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Toutes les demandes
        </Link>
        {/*
          Toujours offert, même sans soumission validée : l'écran de
          comparaison sait dire « personne n'a encore répondu », et cacher le
          lien laisserait croire que la fonction n'existe pas.
        */}
        <Link
          to={`/rfqs/${rfqId}/comparison`}
          className="inline-flex items-center gap-1.5 text-[13px] text-info hover:underline"
        >
          <Scale className="size-4" aria-hidden />
          Comparer les soumissions
        </Link>
      </div>

      {!draft && <FrozenNotice detail={data} />}
      {rfq.status === "READY_TO_SEND" && (
        <DispatchSection rfqId={rfqId} onSend={() => setSending(true)} />
      )}
      {draft && <ValidationNotice detail={data} />}
      {draft && validation.stale_lines.length > 0 && (
        <StaleNotice rfqId={rfqId} stale={validation.stale_lines} />
      )}

      <section className="flex min-w-0 flex-col gap-2">
        <header className="flex items-center justify-between gap-3">
          <h2 className="text-[15px] font-medium text-ink">Fournisseurs consultés</h2>
          {draft && (
            <Button variant="secondary" size="sm" onClick={() => setAddingSupplier(true)}>
              <Plus className="size-4" aria-hidden />
              Ajouter
            </Button>
          )}
        </header>

        {suppliers.length === 0 ? (
          <EmptyState
            title="Aucun fournisseur"
            description="Une demande sans destinataire ne peut pas être figée."
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {suppliers.map((row) => (
              <li key={row.id}>
                <SupplierCard rfqId={rfqId} row={row} lines={lines} draft={draft} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex min-w-0 flex-col gap-2">
        <header className="flex items-center justify-between gap-3">
          <h2 className="text-[15px] font-medium text-ink">Articles demandés</h2>
          {draft && (
            <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
              <Plus className="size-4" aria-hidden />
              Ajouter une ligne
            </Button>
          )}
        </header>

        {lines.length === 0 ? (
          <EmptyState
            title="Aucune ligne"
            description="Écrivez ce dont vous avez besoin. L'article du catalogue est facultatif."
          />
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-lg border border-border bg-surface md:block">
              <TableShell>
                <thead>
                  <tr>
                    <Th className="max-w-0">Article</Th>
                    <Th align="right" className="whitespace-nowrap">
                      Quantité
                    </Th>
                    <Th className="whitespace-nowrap">Provenance</Th>
                    <Th align="right" className="whitespace-nowrap">
                      Demandé à
                    </Th>
                    {draft && <Th className="w-10" />}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <LineRow key={line.id} rfqId={rfqId} line={line} draft={draft} />
                  ))}
                </tbody>
              </TableShell>
            </div>

            <ul className="flex flex-col gap-2 md:hidden">
              {lines.map((line) => (
                <li key={line.id}>
                  <LineCard line={line} />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <RequestedTerms terms={rfq.requested_terms} />

      <AddLineDialog rfqId={rfqId} open={adding} onOpenChange={setAdding} />
      <AddSupplierDialog
        rfqId={rfqId}
        open={addingSupplier}
        onOpenChange={setAddingSupplier}
        alreadyThere={suppliers.map((row) => row.supplier_id)}
      />
      <PreviewDialog rfqId={rfqId} open={previewing} onOpenChange={setPreviewing} />
      <SendDialog rfqId={rfqId} open={sending} onOpenChange={setSending} />
    </Page>
  );
}

/**
 * Ce que « figée » veut dire, écrit noir sur blanc.
 *
 * Le mot « envoyée » n'apparaît nulle part : rien n'a été transmis, et l'écran
 * doit le dire plutôt que de laisser l'utilisateur le supposer.
 */
function FrozenNotice({ detail }: { detail: RfqDetail }) {
  const { rfq } = detail;
  if (rfq.status === "CANCELLED") {
    return (
      <div className="rounded-lg border border-border bg-surface-raised p-3 text-[13px] text-ink-muted">
        Demande annulée{rfq.cancelled_by ? ` par ${rfq.cancelled_by}` : ""}
        {rfq.cancelled_at ? ` le ${formatDate(rfq.cancelled_at)}` : ""}. Son contenu reste
        lisible.
        {rfq.cancellation_reason && (
          <span className="mt-1 block text-ink-subtle">{rfq.cancellation_reason}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-info/30 bg-info-subtle p-3 text-[13px]">
      <p className="flex items-center gap-2 font-medium text-ink">
        <Lock className="size-4 shrink-0" aria-hidden />
        Contenu figé — pas encore transmis
      </p>
      <p className="text-ink-muted">
        Le document est arrêté depuis {rfq.finalized_at ? formatDate(rfq.finalized_at) : "—"}
        {rfq.finalized_by ? ` (${rfq.finalized_by})` : ""}. HD-Vault n'envoie rien : copiez
        l'aperçu et transmettez-le vous-même.
      </p>
      {rfq.content_hash && (
        <p className="font-mono text-[11px] text-ink-subtle">
          Empreinte d'intégrité {rfq.content_hash.slice(0, 16)}…
        </p>
      )}
      {rfq.finalization_warnings.length > 0 && (
        <ul className="list-inside list-disc text-ink-muted">
          {rfq.finalization_warnings.map((warning) => (
            <li key={warning}>{formatRfqWarning(warning)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Ce qui bloque, et ce qui mérite seulement d'être lu. */
function ValidationNotice({ detail }: { detail: RfqDetail }) {
  const { blockers, warnings, can_finalize } = detail.validation;
  if (blockers.length === 0 && warnings.length === 0) return null;

  return (
    <div
      className={`flex flex-col gap-1.5 rounded-lg border p-3 text-[13px] ${
        can_finalize
          ? "border-warning/30 bg-warning-subtle"
          : "border-border bg-surface-raised"
      }`}
    >
      {blockers.length > 0 && (
        <>
          <p className="font-medium text-ink">Il manque quelque chose pour figer</p>
          <ul className="list-inside list-disc text-ink-muted">
            {blockers.map((item) => (
              <li key={item}>{formatRfqWarning(item)}</li>
            ))}
          </ul>
        </>
      )}
      {warnings.length > 0 && (
        <>
          <p className="font-medium text-ink">À lire avant de figer</p>
          <ul className="list-inside list-disc text-ink-muted">
            {warnings.map((item) => (
              <li key={item}>{formatRfqWarning(item)}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Le besoin net a bougé depuis la préparation.
 *
 * **On montre les deux chiffres et on laisse choisir.** Corriger 17 en 5 tout
 * seul reviendrait à décider d'un achat à la place de quelqu'un : la quantité
 * demandée est commerciale, elle a pu être choisie en connaissance de cause.
 */
function StaleNotice({ rfqId, stale }: { rfqId: Uuid; stale: RfqStaleLine[] }) {
  const toast = useToast();
  const refresh = useRefreshRfqLine();

  async function take(lineId: Uuid) {
    try {
      await refresh.mutateAsync({ rfqId, lineId });
      toast.success("Quantité reprise du besoin net");
    } catch (failure) {
      toast.error("Quantité non reprise", String(failure));
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-warning/30 bg-warning-subtle p-3 text-[13px]">
      <p className="flex items-center gap-2 font-medium text-ink">
        <AlertTriangle className="size-4 shrink-0" aria-hidden />
        Le besoin net a changé depuis la préparation
      </p>
      <p className="text-ink-muted">
        Rien n'a été modifié. Gardez la quantité demandée, ou reprenez celle du besoin actuel.
      </p>
      <ul className="flex flex-col gap-1.5">
        {stale.map((line) => (
          <li
            key={line.line_id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-surface px-2.5 py-2"
          >
            <span className="min-w-0 flex-1 truncate text-ink">{line.description}</span>
            <span className="tnum whitespace-nowrap text-ink-muted">
              demandé {formatQuantity(line.requested_quantity)} · besoin actuel{" "}
              {formatQuantity(line.net_shortage_now)}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={refresh.isPending}
              onClick={() => void take(line.line_id)}
            >
              <RefreshCw className="size-3.5" aria-hidden />
              Reprendre {formatQuantity(line.net_shortage_now)}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Un destinataire, et exactement les lignes qu'il reçoit. */
function SupplierCard({
  rfqId,
  row,
  lines,
  draft,
}: {
  rfqId: Uuid;
  row: RfqSupplierRow;
  lines: RfqLine[];
  draft: boolean;
}) {
  const toast = useToast();
  const setAllocation = useSetRfqAllocation();
  const removeSupplier = useRemoveRfqSupplier();
  const [open, setOpen] = useState(false);

  const chosen = new Set(
    lines.filter((line) => line.supplier_ids.includes(row.supplier_id)).map((line) => line.id),
  );

  async function toggle(lineId: Uuid) {
    const next = new Set(chosen);
    if (next.has(lineId)) next.delete(lineId);
    else next.add(lineId);
    try {
      await setAllocation.mutateAsync({
        rfqId,
        rfqSupplierId: row.id,
        line_ids: [...next],
      });
    } catch (failure) {
      toast.error("Affectation non enregistrée", String(failure));
    }
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <div className="flex flex-wrap items-center gap-2 p-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-medium text-ink">{row.supplier_name}</p>
          <p className="truncate text-[12px] text-ink-muted">
            {row.contact_email ?? "Aucune adresse courriel"}
            {row.contact_name ? ` · ${row.contact_name}` : ""}
          </p>
        </div>
        <span className="tnum whitespace-nowrap text-[12px] text-ink-muted">
          {pluralize(row.line_count, "ligne")}
        </span>
        {!row.has_contact && (
          <StatusBadge tone="warning" className="shrink-0">
            Sans adresse
          </StatusBadge>
        )}
        {!row.supplier_active && (
          <StatusBadge tone="out" className="shrink-0">
            Inactif
          </StatusBadge>
        )}
        {draft && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setOpen((value) => !value)}>
              {open ? "Fermer" : "Choisir les lignes"}
            </Button>
            <button
              type="button"
              aria-label={`Retirer ${row.supplier_name}`}
              className="rounded-md p-2 text-ink-subtle hover:text-danger"
              onClick={() =>
                void removeSupplier
                  .mutateAsync({ rfqId, rfqSupplierId: row.id })
                  .catch((failure: unknown) =>
                    toast.error("Fournisseur non retiré", String(failure)),
                  )
              }
            >
              <Trash2 className="size-4" aria-hidden />
            </button>
          </>
        )}
      </div>

      {draft && open && (
        <ul className="divide-y divide-border/60 border-t border-border">
          {lines.map((line) => (
            <li key={line.id}>
              <label className="flex min-h-[44px] cursor-pointer items-center gap-3 px-3 py-2 text-[13px] hover:bg-surface-hover">
                <input
                  type="checkbox"
                  checked={chosen.has(line.id)}
                  onChange={() => void toggle(line.id)}
                  className="size-4 shrink-0 accent-brand"
                  aria-label={`Demander ${line.description} à ${row.supplier_name}`}
                />
                <span className="min-w-0 flex-1 truncate text-ink">{line.description}</span>
                <span className="tnum whitespace-nowrap text-ink-muted">
                  {formatQuantity(line.requested_quantity)} {line.unit}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LineRow({ rfqId, line, draft }: { rfqId: Uuid; line: RfqLine; draft: boolean }) {
  const toast = useToast();
  const remove = useRemoveRfqLine();

  return (
    <Tr>
      <Td className="max-w-0">
        <span className="block truncate font-medium text-ink">{line.description}</span>
        <span className="block truncate text-[11px] text-ink-subtle">
          {line.manufacturer ? `${line.manufacturer} ` : ""}
          {line.mpn ?? ""}
          {!line.product_id ? " · hors catalogue" : ""}
        </span>
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
        {formatQuantity(line.requested_quantity)} {line.unit}
      </Td>
      <Td className="whitespace-nowrap text-[12px] text-ink-muted">
        {formatRfqLineSource(line.source)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {line.supplier_ids.length === 0 ? "—" : line.supplier_ids.length}
      </Td>
      {draft && (
        <Td>
          <button
            type="button"
            aria-label={`Retirer ${line.description}`}
            className="rounded-md p-2 text-ink-subtle hover:text-danger"
            onClick={() =>
              void remove
                .mutateAsync({ rfqId, lineId: line.id })
                .catch((failure: unknown) => toast.error("Ligne non retirée", String(failure)))
            }
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        </Td>
      )}
    </Tr>
  );
}

function LineCard({ line }: { line: RfqLine }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <p className="truncate text-[14px] font-medium text-ink">{line.description}</p>
      <p className="tnum mt-0.5 text-[13px] text-ink-muted">
        {formatQuantity(line.requested_quantity)} {line.unit} ·{" "}
        {line.supplier_ids.length === 0
          ? "aucun fournisseur"
          : pluralize(line.supplier_ids.length, "fournisseur")}
      </p>
      <p className="mt-0.5 text-[12px] text-ink-subtle">{formatRfqLineSource(line.source)}</p>
    </div>
  );
}

/** Ce qu'on demande au fournisseur de répondre. */
function RequestedTerms({ terms }: { terms: string[] }) {
  if (terms.length === 0) return null;
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h2 className="text-[15px] font-medium text-ink">Questions posées</h2>
      <ul className="flex flex-wrap gap-1.5">
        {terms.map((term) => (
          <li key={term}>
            <StatusBadge tone="unknown">{formatRfqTerm(term)}</StatusBadge>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Ce que chaque fournisseur lirait.
 *
 * Le dialogue ne comporte aucun bouton d'envoi. On peut copier le texte : c'est
 * la seule façon de transmettre dans cette version, et elle est explicite.
 */
function PreviewDialog({
  rfqId,
  open,
  onOpenChange,
}: {
  rfqId: Uuid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading } = useRfqDispatchPreview(rfqId, { enabled: open });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Aperçu du document"
      description="Ce qu'un fournisseur lirait. HD-Vault n'envoie rien : copiez le texte."
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          Fermer
        </Button>
      }
    >
      {isLoading && <LoadingState />}
      <div className="flex flex-col gap-4">
        {data?.map((item) => (
          <article key={item.supplier_name} className="flex flex-col gap-1.5">
            <header className="flex flex-wrap items-center gap-2">
              <span className="text-[14px] font-medium text-ink">{item.supplier_name}</span>
              <span className="text-[12px] text-ink-muted">
                {item.recipient ?? "aucune adresse"}
              </span>
              {!item.is_final && (
                <StatusBadge tone="unknown">Brouillon — non figé</StatusBadge>
              )}
            </header>
            <p className="text-[13px] font-medium text-ink">{item.subject}</p>
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface-raised p-3 text-[12px] text-ink-muted">
              {item.body}
            </pre>
          </article>
        ))}
      </div>
    </Dialog>
  );
}

/**
 * L'état de la transmission, et le seul bouton d'envoi du produit (V1.6-05).
 *
 * Trois choses doivent être vraies sur cet écran, et chacune coûte une ligne
 * de code exprès :
 *
 * 1. **on ne montre jamais « envoyée » si rien n'est parti.** L'état vient des
 *    messages réellement tentés, pas du statut de la demande ;
 * 2. **quand rien n'est configuré, on le dit.** Un bouton qui laisse croire
 *    qu'un fournisseur va recevoir quelque chose est pire que pas de bouton ;
 * 3. **une livraison incertaine ne propose pas de renvoyer.** Elle propose
 *    d'aller vérifier.
 */
function DispatchSection({ rfqId, onSend }: { rfqId: Uuid; onSend: () => void }) {
  const { data: email } = useEmailStatus();
  const { data: rows, isLoading } = useRfqDispatch(rfqId);
  const { data: messages } = useRfqOutboundMessages(rfqId);

  if (isLoading || !rows) return null;
  const eligible = rows.filter((row) => row.can_dispatch);

  return (
    <section className="flex min-w-0 flex-col gap-2">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[15px] font-medium text-ink">Transmission</h2>
        <div className="flex items-center gap-2">
          <EmailStatusBadge status={email} />
          <Button
            variant="primary"
            size="sm"
            disabled={eligible.length === 0}
            onClick={onSend}
          >
            <Send className="size-4" aria-hidden />
            Envoyer
          </Button>
        </div>
      </header>

      {email && !email.enabled && (
        <p className="rounded-lg border border-border bg-surface-raised p-3 text-[13px] text-ink-muted">
          Envoi e-mail non configuré sur cette installation. HD-Vault ne transmet rien :
          utilisez l'aperçu et envoyez le message vous-même.
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li key={row.rfq_supplier_id}>
            <DispatchRow row={row} />
          </li>
        ))}
      </ul>

      {messages && messages.length > 0 && <OutboundHistory messages={messages} />}
    </section>
  );
}

function EmailStatusBadge({ status }: { status: EmailStatus | undefined }) {
  if (!status) return null;
  return (
    <StatusBadge tone={status.enabled ? "info" : "unknown"}>
      {status.enabled ? `E-mail : ${status.provider}` : "E-mail : non configuré"}
    </StatusBadge>
  );
}

function DispatchRow({ row }: { row: RfqDispatchRow }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface p-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{row.supplier_name}</p>
        <p className="truncate text-[12px] text-ink-muted">
          {row.contact_email ?? "Aucune adresse courriel"}
        </p>
        {row.state === "UNKNOWN_DELIVERY" && (
          <p className="mt-1 flex items-center gap-1.5 text-[12px] text-warning">
            <MailWarning className="size-3.5 shrink-0" aria-hidden />
            Livraison incertaine — ne pas renvoyer sans vérification.
          </p>
        )}
        {row.state === "FAILED" && row.last_error && (
          <p className="mt-1 text-[12px] text-ink-muted">{formatSendError(row.last_error)}</p>
        )}
      </div>
      {row.sent_at && (
        <span className="whitespace-nowrap text-[12px] text-ink-muted">
          {formatDateTime(row.sent_at)}
        </span>
      )}
      <StatusBadge tone={dispatchTone(row.state)} className="shrink-0">
        {formatDispatchState(row.state)}
      </StatusBadge>
    </div>
  );
}

function OutboundHistory({ messages }: { messages: OutboundMessage[] }) {
  return (
    <details className="rounded-lg border border-border bg-surface p-3 text-[13px]">
      <summary className="cursor-pointer text-ink-muted">
        {pluralize(messages.length, "tentative")} — voir l'historique
      </summary>
      <ul className="mt-2 flex flex-col gap-1.5">
        {messages.map((message) => (
          <li key={message.id} className="flex flex-wrap items-center gap-2 text-ink-muted">
            <span className="whitespace-nowrap">{formatDateTime(message.queued_at)}</span>
            <span className="min-w-0 flex-1 truncate">{message.to_email}</span>
            <span className="tnum whitespace-nowrap">essai {message.attempts}</span>
            <StatusBadge tone={dispatchTone(message.status)} className="shrink-0">
              {formatDispatchState(message.status)}
            </StatusBadge>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * La confirmation d'envoi.
 *
 * **Le bouton n'envoie pas.** Il ouvre ceci, où le destinataire, l'objet et le
 * corps complet sont sous les yeux de la personne avant qu'elle décide. C'est
 * la seule protection qui vaille contre l'envoi qu'on regrette : une fois
 * qu'un courriel est parti, aucun bouton ne le rattrape.
 */
function SendDialog({
  rfqId,
  open,
  onOpenChange,
}: {
  rfqId: Uuid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const { data: email } = useEmailStatus();
  const { data: rows } = useRfqDispatch(rfqId, { enabled: open });
  const { data: previews } = useRfqDispatchPreview(rfqId, { enabled: open });
  const send = useSendRfq();

  const eligible = (rows ?? []).filter((row) => row.can_dispatch);
  const byName = new Map((previews ?? []).map((item) => [item.supplier_name, item]));

  async function confirm() {
    try {
      const result = await send.mutateAsync({ rfqId });
      const sent = result.filter((item) => item.status === "SENT").length;
      const failed = result.filter((item) => item.status === "FAILED").length;
      const unknown = result.filter(
        (item) => item.status === "UNKNOWN_DELIVERY",
      ).length;

      if (sent > 0) {
        toast.success(
          "Message transmis",
          `${pluralize(sent, "message")} accepté(s) par le service d'envoi.`,
        );
      }
      if (unknown > 0) {
        toast.error(
          "Livraison incertaine",
          "Le service n'a pas confirmé. Vérifiez auprès du fournisseur avant de renvoyer.",
        );
      }
      if (failed > 0 && sent === 0 && unknown === 0) {
        toast.error(
          "Rien n'a été transmis",
          formatSendError(result[0]?.last_error ?? null) ?? "Envoi refusé.",
        );
      }
      onOpenChange(false);
    } catch (failure) {
      toast.error("Envoi non effectué", String(failure));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Confirmer l'envoi"
      description={
        email?.enabled
          ? "Cette action placera ces messages dans la file d'envoi. Ils pourront être transmis aux fournisseurs."
          : "Envoi e-mail non configuré : la demande sera inscrite comme tentée, et rien ne sera transmis."
      }
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void confirm()}
            disabled={eligible.length === 0 || send.isPending}
          >
            {send.isPending
              ? "Envoi…"
              : `Envoyer à ${pluralize(eligible.length, "fournisseur")}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {eligible.length === 0 && (
          <p className="text-[13px] text-ink-muted">
            Aucun destinataire n'a d'adresse courriel sur le document approuvé.
          </p>
        )}
        {eligible.map((row) => {
          const preview = byName.get(row.supplier_name);
          return (
            <article
              key={row.rfq_supplier_id}
              className="flex flex-col gap-1.5 rounded-lg border border-border p-3"
            >
              <p className="text-[14px] font-medium text-ink">{row.supplier_name}</p>
              <p className="text-[13px] text-ink-muted">À : {row.contact_email}</p>
              {preview && (
                <>
                  <p className="text-[13px] font-medium text-ink">{preview.subject}</p>
                  <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded-md bg-surface-raised p-2.5 text-[12px] text-ink-muted">
                    {preview.body}
                  </pre>
                  <p className="text-[11px] text-ink-subtle">
                    {pluralize(preview.lines.length, "ligne")}
                    {preview.content_hash
                      ? ` · empreinte ${preview.content_hash.slice(0, 12)}…`
                      : ""}
                  </p>
                </>
              )}
            </article>
          );
        })}
      </div>
    </Dialog>
  );
}

function AddSupplierDialog({
  rfqId,
  open,
  onOpenChange,
  alreadyThere,
}: {
  rfqId: Uuid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alreadyThere: Uuid[];
}) {
  const toast = useToast();
  const addSupplier = useAddRfqSupplier();
  const [supplierId, setSupplierId] = useState("");
  const { data } = useSuppliers({ limit: 200 }, { enabled: open });

  const available = (data?.items ?? []).filter((item) => !alreadyThere.includes(item.id));

  async function submit() {
    if (!supplierId) return;
    try {
      await addSupplier.mutateAsync({ rfqId, supplier_id: supplierId });
      setSupplierId("");
      onOpenChange(false);
    } catch (failure) {
      toast.error("Fournisseur non ajouté", String(failure));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Ajouter un fournisseur"
      description="Choisissez ensuite les lignes qu'il recevra."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!supplierId || addSupplier.isPending}
          >
            {addSupplier.isPending ? "Ajout…" : "Ajouter"}
          </Button>
        </>
      }
    >
      <Field label="Fournisseur">
        <Select
          value={supplierId}
          onChange={(event) => setSupplierId(event.target.value)}
          aria-label="Fournisseur"
        >
          <option value="">Choisir…</option>
          {available.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
              {item.email ? "" : " — sans courriel"}
            </option>
          ))}
        </Select>
      </Field>
    </Dialog>
  );
}

function AddLineDialog({
  rfqId,
  open,
  onOpenChange,
}: {
  rfqId: Uuid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const addLine = useAddRfqLine();
  const [description, setDescription] = useState("");
  const [term, setTerm] = useState("");
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState<UnitCode>("EA");

  const { data: products } = useInventory(
    { q: term.trim() || undefined, limit: 25 },
    { enabled: open && term.trim().length > 1 },
  );

  function reset() {
    setDescription("");
    setTerm("");
    setProductId("");
    setQuantity("1");
    setUnit("EA");
  }

  async function submit() {
    if (!quantity || (!description.trim() && !productId)) return;
    try {
      await addLine.mutateAsync({
        rfqId,
        description: description.trim() || null,
        product_id: productId || null,
        requested_quantity: quantity,
        unit: productId ? null : unit,
      });
      reset();
      onOpenChange(false);
    } catch (failure) {
      toast.error("Ligne non ajoutée", String(failure));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Ajouter une ligne"
      description="Écrivez ce qu'il vous faut. L'article du catalogue est facultatif."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={(!description.trim() && !productId) || !quantity || addLine.isPending}
          >
            {addLine.isPending ? "Ajout…" : "Ajouter"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field
          label="Ce qu'il vous faut"
          hint="En toutes lettres. « boîtier inox 400x300 » est une ligne valide."
        >
          <Input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Description"
            aria-label="Description de la ligne"
          />
        </Field>

        <Field label="Article du catalogue" hint="Facultatif.">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Nom, fabricant, MPN…"
              className="pl-9"
              aria-label="Chercher un article"
            />
          </div>
          <Select
            value={productId}
            onChange={(event) => setProductId(event.target.value)}
            aria-label="Article du catalogue"
            className="mt-2"
          >
            <option value="">Aucun — ligne libre</option>
            {products?.items.map((row) => (
              <option key={row.product_id} value={row.product_id}>
                {row.name}
                {row.part_number ? ` (${row.part_number})` : ""}
              </option>
            ))}
          </Select>
        </Field>

        <div className="flex gap-3">
          <div className="flex-1">
            <Field label="Quantité">
              <Input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                aria-label="Quantité"
              />
            </Field>
          </div>
          {!productId && (
            <div className="w-32">
              <Field label="Unité">
                <Select
                  value={unit}
                  onChange={(event) => setUnit(event.target.value as UnitCode)}
                  aria-label="Unité"
                >
                  {STOCKABLE_UNITS.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
